import React, { useState, useRef, useEffect } from 'react';
import { Hash, Bell, Pin, Users, Search, Inbox, HelpCircle, PlusCircle, Image, Smile, Send, MoreHorizontal, Reply, Heart, Volume2, Sparkles, AtSign, FileText, Bookmark, Star, Gift, Zap } from 'lucide-react';
import './ChatArea.css';

const generateMessages = (channelName) => {
  const sampleMessages = [
    { id: 1, user: 'Alex', avatar: 'A', content: `Welcome to #${channelName}! Feel free to chat here.`, time: '10:30 AM', color: '#00d4aa', gradient: 'linear-gradient(135deg, #00d4aa 0%, #00a8ff 100%)', isPinned: true },
    { id: 2, user: 'Jordan', avatar: 'J', content: "Hey everyone! How's it going?", time: '10:32 AM', color: '#00a8ff', gradient: 'linear-gradient(135deg, #00a8ff 0%, #0066ff 100%)' },
    { id: 3, user: 'Sam', avatar: 'S', content: 'Just joined the server, excited to be here!', time: '10:35 AM', color: '#ff6b9d', gradient: 'linear-gradient(135deg, #ff6b9d 0%, #ff8c42 100%)' },
    { id: 4, user: 'Alex', avatar: 'A', content: 'Welcome Sam! Make yourself at home. 🎉', time: '10:36 AM', color: '#00d4aa', gradient: 'linear-gradient(135deg, #00d4aa 0%, #00a8ff 100%)' },
    { id: 5, user: 'Taylor', avatar: 'T', content: 'Anyone want to hop in voice chat later?', time: '10:40 AM', color: '#a855f7', gradient: 'linear-gradient(135deg, #a855f7 0%, #6366f1 100%)' },
    { id: 6, user: 'Jordan', avatar: 'J', content: "I'm down! What time works for everyone?", time: '10:42 AM', color: '#00a8ff', gradient: 'linear-gradient(135deg, #00a8ff 0%, #0066ff 100%)' },
    { id: 7, user: 'Riley', avatar: 'R', content: 'This new FEK app is pretty cool honestly 🔥', time: '10:45 AM', color: '#f59e0b', gradient: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)' },
    { id: 8, user: 'Sam', avatar: 'S', content: 'The UI is so clean! Love the dark theme', time: '10:47 AM', color: '#ff6b9d', gradient: 'linear-gradient(135deg, #ff6b9d 0%, #ff8c42 100%)' },
    { id: 9, user: 'Morgan', avatar: 'M', content: 'Has anyone tried the new commander channel feature?', time: '10:50 AM', color: '#22c55e', gradient: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)' },
    { id: 10, user: 'Alex', avatar: 'A', content: "Yeah it's great for coordinating with the team!", time: '10:52 AM', color: '#00d4aa', gradient: 'linear-gradient(135deg, #00d4aa 0%, #00a8ff 100%)' },
    { id: 11, user: 'Casey', avatar: 'C', content: 'Just got back online. What did I miss?', time: '10:55 AM', color: '#ec4899', gradient: 'linear-gradient(135deg, #ec4899 0%, #d946ef 100%)' },
    { id: 12, user: 'Taylor', avatar: 'T', content: 'Not much, we were just chatting about the voice channels', time: '10:57 AM', color: '#a855f7', gradient: 'linear-gradient(135deg, #a855f7 0%, #6366f1 100%)' },
  ];
  return sampleMessages;
};

function ChatArea({ serverId, channel, username }) {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [showPinnedBanner, setShowPinnedBanner] = useState(true);
  const [typingUsers, setTypingUsers] = useState(['Alex', 'Jordan']);
  const messagesEndRef = useRef(null);

  // Simulate typing indicator
  useEffect(() => {
    const interval = setInterval(() => {
      const users = ['Alex', 'Jordan', 'Sam', 'Taylor', 'Riley'];
      const count = Math.floor(Math.random() * 3);
      const typing = users.slice(0, count);
      setTypingUsers(typing);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const loadMessages = async () => {
      if (channel.type === 'text') {
        if (window.electronAPI) {
          const savedMessages = await window.electronAPI.getMessages(serverId, channel.id);
          if (savedMessages && savedMessages.length > 0) {
            setMessages(savedMessages);
          } else {
            // Show sample messages for new channels to avoid empty feeling
            setMessages(generateMessages(channel.name || 'general'));
          }
        } else {
          setMessages(generateMessages(channel.name || 'general'));
        }
      }
    };
    loadMessages();
  }, [channel, serverId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSend = async () => {
    if (!inputValue.trim()) return;
    
    const newMessage = {
      id: Date.now(),
      user: username,
      avatar: username[0].toUpperCase(),
      content: inputValue,
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      color: '#d4a574',
      gradient: 'linear-gradient(135deg, #d4a574 0%, #e8c49a 100%)'
    };
    
    setMessages(prev => [...prev, newMessage]);
    setInputValue('');
    
    // Save to persistent storage
    if (window.electronAPI) {
      await window.electronAPI.saveMessage(serverId, channel.id, newMessage);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (channel.type === 'voice') {
    return (
      <div className="chat-area">
        <div className="chat-header">
          <div className="header-left">
            <div className="header-icon-wrapper voice">
              <Volume2 size={18} />
            </div>
            <span className="header-title">{channel.name}</span>
            <span className="header-badge">Voice</span>
          </div>
        </div>
        <div className="voice-placeholder">
          <div className="voice-info">
            <div className="voice-icon-large">
              <Volume2 size={48} />
            </div>
            <h2>Voice Channel</h2>
            <p>You're viewing a voice channel. Click to join and start talking!</p>
            <button className="join-voice-btn">
              <Sparkles size={18} />
              Join Voice
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="chat-area">
      <div className="chat-header">
        <div className="header-left">
          <div className="header-icon-wrapper">
            <Hash size={18} />
          </div>
          <span className="header-title">{channel.name || 'general'}</span>
          <div className="header-divider" />
          <span className="header-description">Chat with everyone here</span>
        </div>
        <div className="header-right">
          <button className="header-btn" title="Notifications"><Bell size={18} /></button>
          <button className="header-btn" title="Pinned Messages"><Pin size={18} /></button>
          <button className="header-btn active" title="Member List"><Users size={18} /></button>
          <div className="search-box">
            <Search size={15} />
            <input type="text" placeholder="Search messages..." />
          </div>
          <button className="header-btn" title="Inbox"><Inbox size={18} /></button>
          <button className="header-btn" title="Help"><HelpCircle size={18} /></button>
        </div>
      </div>

      {showPinnedBanner && (
        <div className="pinned-banner">
          <div className="pinned-content">
            <Pin size={14} />
            <span><strong>1 pinned message</strong> in this channel</span>
          </div>
          <button className="pinned-view" onClick={() => setShowPinnedBanner(false)}>View</button>
        </div>
      )}

      <div className="messages-container">
        {messages.length === 0 && (
          <div className="channel-welcome">
            <div className="welcome-icon">
              <Hash size={36} />
            </div>
            <h1>Welcome to #{channel.name || 'general'}</h1>
            <p>This is the beginning of the #{channel.name || 'general'} channel. Start the conversation!</p>
            <div className="welcome-actions">
              <button className="welcome-btn"><Smile size={16} /> Add Reaction</button>
              <button className="welcome-btn"><AtSign size={16} /> Mention</button>
            </div>
          </div>
        )}

        {messages.map((message) => (
          <div key={message.id} className="message">
            <div className="message-avatar" style={{ background: message.gradient || message.color }}>
              {message.avatar}
            </div>
            <div className="message-content">
              <div className="message-header">
                <span className="message-username" style={{ color: message.color }}>
                  {message.user}
                </span>
                <span className="message-time">{message.time}</span>
              </div>
              <div className="message-text">{message.content}</div>
            </div>
            <div className="message-actions">
              <button title="React"><Smile size={16} /></button>
              <button title="Reply"><Reply size={16} /></button>
              <button title="Save"><Heart size={16} /></button>
              <button title="More"><MoreHorizontal size={16} /></button>
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      <div className="message-input-container">
        {typingUsers.length > 0 && (
          <div className="typing-indicator">
            <div className="typing-dots">
              <span></span>
              <span></span>
              <span></span>
            </div>
            <span className="typing-text">
              {typingUsers.length === 1 
                ? `${typingUsers[0]} is typing...`
                : typingUsers.length === 2
                ? `${typingUsers[0]} and ${typingUsers[1]} are typing...`
                : `${typingUsers[0]} and ${typingUsers.length - 1} others are typing...`
              }
            </span>
          </div>
        )}
        <div className="message-input-wrapper">
          <button className="input-btn attach" title="Attach File">
            <PlusCircle size={22} />
          </button>
          <input
            type="text"
            className="message-input"
            placeholder={`Message #${channel.name || 'general'}`}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <div className="input-actions">
            <button className="input-btn" title="Send Gift"><Gift size={20} /></button>
            <button className="input-btn" title="Upload Image"><Image size={20} /></button>
            <button className="input-btn" title="Emoji"><Smile size={20} /></button>
            <button 
              className={`input-btn send-btn ${inputValue.trim() ? 'active' : ''}`}
              onClick={handleSend}
              title="Send Message"
            >
              <Send size={18} />
            </button>
          </div>
        </div>
        <div className="input-hint">
          Press <kbd>Enter</kbd> to send, <kbd>Shift + Enter</kbd> for new line
        </div>
      </div>
    </div>
  );
}

export default ChatArea;
